/** The Day ribbon's geometry and words (plan §5 "Days card" → Day ribbon, the surface
 *  brief's FORM): a segment per row, its width proportional to the row's span (min 6 px,
 *  1 px gaps), fitted to the measured width so the ribbon never scrolls
 *  (Axis-Never-Scrolls); each segment's day label ("Day 3–4" → "3–4") shown when the segment
 *  is at least 24 px wide and the label fits inside it, then thinned like the schedule
 *  axis's ticks so neighbouring labels keep a 12 px gap. Edit marks (changed / added /
 *  removed) are said in words in each segment's name. Pure: the component measures. */
import type { ItineraryDay } from '@/api/itineraries';

import { daySpan } from './itineraryDays';

export const RIBBON_HEIGHT_PX = 28;
export const RIBBON_MIN_PX = 6;
export const RIBBON_GAP_PX = 1;
/** A segment narrower than this carries no label. */
export const RIBBON_LABEL_MIN_PX = 24;
/** A tabular caption character (12 px Inter) — the estimate the axis ticks use, rounded up. */
export const RIBBON_CHAR_PX = 7;
/** Room around a label inside its segment (both sides). */
export const RIBBON_LABEL_PAD_PX = 8;
/** Neighbouring labels keep at least this gap (the axis ticks' `TICK_GAP_PX`). */
export const RIBBON_LABEL_GAP_PX = 12;

/** A row of the ribbon: the outline's day, in order — a removed one with the days it covers
 *  as stored (`stored`), which it keeps naming until the save. */
export type RibbonDay = Pick<ItineraryDay, 'id' | 'title' | 'day_span'> & { stored?: DayRange };

/** An unsaved edit to a day (edit mode): a changed day (with its change count when known),
 *  an added one, or one removed until the save. */
export type DayMark =
  | { state: 'changed'; changes?: number }
  | { state: 'added' }
  | { state: 'removed' };

export type DayMarks = Readonly<Record<number, DayMark>>;

/** The days a row covers (1-based, inclusive). */
export interface DayRange {
  start: number;
  end: number;
}

/** Each row's days: rows follow one another, each covering its span. A removed row no
 *  longer counts, so the rows after it read as they will once it is gone; it keeps the days
 *  it covers as stored (`stored`) — "Day 7–11 · Chiang Khong · Removed", never a range
 *  borrowed from the rows the draft keeps. */
export function dayRanges(days: readonly RibbonDay[], marks: DayMarks = {}): DayRange[] {
  let next = 1;
  return days.map((d) => {
    const removed = marks[d.id]?.state === 'removed';
    if (removed && d.stored) return d.stored;
    const range = { start: next, end: next + daySpan(d.day_span) - 1 };
    if (!removed) next = range.end + 1;
    return range;
  });
}

/** "3–4", or "3" for a one-day row. */
export function dayRangeLabel(r: DayRange): string {
  return r.end > r.start ? `${r.start}–${r.end}` : String(r.start);
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

const MARK_WORDS: Readonly<Record<DayMark['state'], string>> = {
  changed: 'Changed',
  added: 'Added',
  removed: 'Removed',
};

/** A segment's accessible name and tooltip: "Day 3–4 · Ayutthaya · 2 changes". */
export function dayName(day: RibbonDay, range: DayRange, mark?: DayMark): string {
  const title = day.title?.trim();
  const words =
    mark?.state === 'changed' && mark.changes
      ? plural(mark.changes, 'change')
      : mark
        ? MARK_WORDS[mark.state]
        : null;
  return [`Day ${dayRangeLabel(range)}`, title, words].filter(Boolean).join(' · ');
}

/** The ribbon's own name: "8 rows · 12 days" (removed rows left out). */
export function ribbonCounts(days: readonly RibbonDay[], marks: DayMarks = {}): string {
  const kept = days.filter((d) => marks[d.id]?.state !== 'removed');
  const total = kept.reduce((sum, d) => sum + daySpan(d.day_span), 0);
  return `${plural(kept.length, 'row')} · ${plural(total, 'day')}`;
}

/** Segment widths in px that fill `widthPx` exactly: proportional to the spans, no segment
 *  under `RIBBON_MIN_PX` (the short rows take the minimum and the rest share what is left,
 *  in proportion). When even that does not fit, the minimum shrinks to an equal share, and
 *  below 2 px a segment the gaps go too — the ribbon never scrolls. Unmeasured (0) → no
 *  widths: the component falls back to the spans as proportions. */
export function ribbonWidths(
  spans: readonly number[],
  widthPx: number,
): { widths: number[]; gap: number } | null {
  const n = spans.length;
  if (!n || !(widthPx > 0)) return null;
  let gap = n > 1 ? RIBBON_GAP_PX : 0;
  if (n * 2 + (n - 1) * gap > widthPx) gap = 0;
  const avail = Math.max(0, widthPx - (n - 1) * gap);
  const min = Math.min(RIBBON_MIN_PX, avail / n);
  const s = spans.map(daySpan);
  const fixed = s.map(() => false);
  let widths = s.map(() => min);
  for (;;) {
    let freeSpan = 0;
    let fixedCount = 0;
    s.forEach((sp, i) => {
      if (fixed[i]) fixedCount += 1;
      else freeSpan += sp;
    });
    const freeAvail = avail - fixedCount * min;
    widths = s.map((sp, i) => (fixed[i] || !freeSpan ? min : (sp / freeSpan) * freeAvail));
    let changed = false;
    widths.forEach((w, i) => {
      if (!fixed[i] && w < min) {
        fixed[i] = true;
        changed = true;
      }
    });
    if (!changed) return { widths, gap };
  }
}

export const ribbonLabelPx = (label: string) => label.length * RIBBON_CHAR_PX;

/** Which labels show, given the segments' widths: a label needs a segment of at least
 *  `RIBBON_LABEL_MIN_PX` that holds it with its padding; then, left to right, a label is
 *  kept only if it starts at least `RIBBON_LABEL_GAP_PX` after the last kept one ends (the
 *  axis ticks' rule). `null` entries never show (a removed row's label would repeat the
 *  next row's number). */
export function thinLabels(
  labels: readonly (string | null)[],
  widths: readonly number[],
  gap: number,
): (string | null)[] {
  let x = 0;
  let lastEnd = -Infinity;
  return labels.map((label, i) => {
    const w = widths[i] ?? 0;
    const left = x;
    x += w + gap;
    if (!label || w < RIBBON_LABEL_MIN_PX) return null;
    const lw = ribbonLabelPx(label);
    if (lw + RIBBON_LABEL_PAD_PX > w) return null;
    const start = left + (w - lw) / 2;
    if (start - lastEnd < RIBBON_LABEL_GAP_PX) return null;
    lastEnd = start + lw;
    return label;
  });
}

export interface RibbonSegment {
  id: number;
  range: DayRange;
  /** The label drawn inside the segment; null when thinned out (the name still says it). */
  label: string | null;
  /** The segment's share of the row: px when measured, else its span. */
  grow: number;
  mark: DayMark | null;
  name: string;
}

/** Everything the ribbon draws, for a measured width (0 = not yet measured: proportions
 *  only, no labels). */
export function ribbonLayout(
  days: readonly RibbonDay[],
  widthPx: number,
  marks: DayMarks = {},
): { segments: RibbonSegment[]; gap: number } {
  const ranges = dayRanges(days, marks);
  const fitted = ribbonWidths(
    days.map((d) => d.day_span),
    widthPx,
  );
  const labels = fitted
    ? thinLabels(
        days.map((d, i) => (marks[d.id]?.state === 'removed' ? null : dayRangeLabel(ranges[i]!))),
        fitted.widths,
        fitted.gap,
      )
    : days.map(() => null);
  return {
    gap: fitted?.gap ?? (days.length > 1 ? RIBBON_GAP_PX : 0),
    segments: days.map((d, i) => ({
      id: d.id,
      range: ranges[i]!,
      label: labels[i] ?? null,
      grow: fitted ? fitted.widths[i]! : daySpan(d.day_span),
      mark: marks[d.id] ?? null,
      name: dayName(d, ranges[i]!, marks[d.id]),
    })),
  };
}
